import React from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";

import Login from "@/pages/Login";
import AppShell from "@/pages/AppShell";
import Dashboard from "@/pages/Dashboard";
import Inventory from "@/pages/Inventory";
import Stock from "@/pages/Stock";
import Requirement from "@/pages/Requirement";
import Purchase from "@/pages/Purchase";
import PurchaseOrders from "@/pages/PurchaseOrders";
import PrintInvoice from "@/pages/PrintInvoice";
import PrintSaleReceipt from "@/pages/PrintSaleReceipt";
import Users from "@/pages/Users";
import Suppliers from "@/pages/Suppliers";
import PaymentSources from "@/pages/PaymentSources";
import Todo from "@/pages/Todo";
import Sales from "@/pages/Sales";
import Customers from "@/pages/Customers";
import CustomerDetail from "@/pages/CustomerDetail";
import AuditTrail from "@/pages/AuditTrail";
import PrintPO from "@/pages/PrintPO";
import ShopSettings from "@/pages/ShopSettings";
import BackupRestore from "@/pages/BackupRestore";
import PlaceMaster from "@/pages/PlaceMaster";
import ProductDataReview from "@/pages/ProductDataReview";
import ProductProfile from "@/pages/ProductProfile";
// ShopReconciliation removed - functionality integrated into Sales > Drawer tab

import "@/App.css";

// Route to section key mapping
const ROUTE_SECTION_MAP = {
  "/": "dashboard",
  "/inventory": "inventory",
  "/stock": "stock",
  "/requirement": "requirement",
  "/purchase": "purchase",
  "/orders": "orders",
  "/sales": "sales",
  "/customers": "customers",
  "/suppliers": "suppliers",
  "/payment-sources": "payment_sources",
  "/todo": "todo",
  "/users": "users",
  "/activity": "activity",
  "/shop-settings": "shop_settings",
  "/product-data-review": "inventory",  // Access via inventory section
};

function ProtectedRoute() {
  const { user, ready } = useAuth();
  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <Outlet />;
}

function PublicOnlyRoute() {
  const { user, ready } = useAuth();
  if (!ready) return null;
  if (user) return <Navigate to="/" replace />;
  return <Outlet />;
}

// Component to check section access
function SectionGuard({ children, section }) {
  const { user } = useAuth();
  
  // Master & super_user have access to everything
  if (user?.role === "master" || user?.role === "super_user") return children;
  
  // Check allowed_sections
  const allowed = Array.isArray(user?.allowed_sections) ? user.allowed_sections : [];
  
  if (allowed.includes(section)) {
    return children;
  }
  
  // No access - show message
  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] text-center p-8">
      <div className="text-4xl mb-4">🔒</div>
      <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
      <p className="text-muted-foreground">
        You don't have permission to access this section.
        <br />
        Contact your administrator to request access.
      </p>
    </div>
  );
}

// Auto-redirect to first allowed section or show no-access page
function AutoRedirect() {
  const { user } = useAuth();
  const location = useLocation();
  
  // Master & super_user go to dashboard
  if (user?.role === "master" || user?.role === "super_user") {
    return <Dashboard />;
  }
  
  const allowed = Array.isArray(user?.allowed_sections) ? user.allowed_sections : [];
  
  // If user has access to dashboard, show it
  if (allowed.includes("dashboard")) {
    return <Dashboard />;
  }
  
  // Find first allowed section and redirect
  if (allowed.length > 0) {
    const firstSection = allowed[0];
    const route = Object.entries(ROUTE_SECTION_MAP).find(([_, s]) => s === firstSection)?.[0];
    if (route && route !== "/") {
      return <Navigate to={route} replace />;
    }
  }
  
  // No access at all - show message
  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] text-center p-8">
      <div className="text-4xl mb-4">🔒</div>
      <h2 className="text-xl font-semibold mb-2">No Access Granted</h2>
      <p className="text-muted-foreground">
        Your account doesn't have access to any sections yet.
        <br />
        Please contact your administrator to grant access.
      </p>
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route element={<PublicOnlyRoute />}>
              <Route path="/login" element={<Login />} />
            </Route>
            <Route element={<ProtectedRoute />}>
              <Route path="/requirement/print" element={<PrintPO />} />
              <Route path="/orders/:poId/print" element={<PrintInvoice />} />
              <Route path="/sales/:id/print" element={<PrintSaleReceipt />} />
              <Route element={<AppShell />}>
                <Route path="/" element={<AutoRedirect />} />
                <Route path="/inventory" element={<SectionGuard section="inventory"><Inventory /></SectionGuard>} />
                <Route path="/stock" element={<SectionGuard section="stock"><Stock /></SectionGuard>} />
                <Route path="/requirement" element={<SectionGuard section="requirement"><Requirement /></SectionGuard>} />
                <Route path="/purchase" element={<SectionGuard section="purchase"><Purchase /></SectionGuard>} />
                <Route path="/orders" element={<SectionGuard section="orders"><PurchaseOrders /></SectionGuard>} />
                <Route path="/sales" element={<SectionGuard section="sales"><Sales /></SectionGuard>} />
                <Route path="/customers" element={<SectionGuard section="customers"><Customers /></SectionGuard>} />
                <Route path="/customers/:customerId" element={<SectionGuard section="customers"><CustomerDetail /></SectionGuard>} />
                <Route path="/suppliers" element={<SectionGuard section="suppliers"><Suppliers /></SectionGuard>} />
                <Route path="/payment-sources" element={<SectionGuard section="payment_sources"><PaymentSources /></SectionGuard>} />
                <Route path="/todo" element={<SectionGuard section="todo"><Todo /></SectionGuard>} />
                <Route path="/users" element={<SectionGuard section="users"><Users /></SectionGuard>} />
                <Route path="/activity" element={<SectionGuard section="activity"><AuditTrail /></SectionGuard>} />
                <Route path="/shop-settings" element={<SectionGuard section="shop_settings"><ShopSettings /></SectionGuard>} />
                <Route path="/places" element={<SectionGuard section="customers"><PlaceMaster /></SectionGuard>} />
                <Route path="/backup" element={<BackupRestore />} />
                <Route path="/product-data-review" element={<ProductDataReview />} />
                <Route path="/inventory/products/:productId" element={<SectionGuard section="inventory"><ProductProfile /></SectionGuard>} />
                {/* Reconciliation integrated into Sales > Drawer tab */}
              </Route>
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster richColors position="top-right" />
      </AuthProvider>
    </div>
  );
}

export default App;
